import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getScenes, createScene, startScene, addSceneNote, endScene, archiveScene, deleteScene, extractErrorMessage } from '../services/api';
import { FaPlus, FaPlay, FaStop, FaArchive, FaTrash, FaStickyNote } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Scenes.css';

function Scenes({ user }) {
  const toast = useToast();
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [selectedScene, setSelectedScene] = useState(null);
  const [savingCreate, setSavingCreate] = useState(false);
  const [formErrorCreate, setFormErrorCreate] = useState(null);
  const [savingNote, setSavingNote] = useState(false);
  const [formErrorNote, setFormErrorNote] = useState(null);
  const [savingEnd, setSavingEnd] = useState(false);
  const [formErrorEnd, setFormErrorEnd] = useState(null);
  const [newScene, setNewScene] = useState({
    title: '',
    description: '',
    setting: '',
    characters: '',
    type: 'rp',
    planned_date: ''
  });
  const [noteText, setNoteText] = useState('');
  const [endData, setEndData] = useState({ summary: '', rating: 5 });

  useEffect(() => {
    fetchScenes();
  }, [filter]);

  const fetchScenes = async () => {
    try {
      setLoading(true);
      const data = await getScenes({ status: filter !== 'all' ? filter : undefined });
      setScenes(data.scenes || data);
    } catch (error) {
      console.error('Error fetching scenes:', error);
      toast.error(extractErrorMessage(error, 'Szenen konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (savingCreate) return;
    setSavingCreate(true);
    setFormErrorCreate(null);
    try {
      await createScene({
        ...newScene,
        planned_date: newScene.planned_date ? new Date(newScene.planned_date).toISOString() : null
      });
      setNewScene({ title: '', description: '', setting: '', characters: '', type: 'rp', planned_date: '' });
      setShowCreateModal(false);
      toast.success('Szene erstellt');
      fetchScenes();
    } catch (error) {
      console.error('Error creating scene:', error);
      setFormErrorCreate(extractErrorMessage(error, 'Fehler beim Erstellen der Szene'));
    } finally {
      setSavingCreate(false);
    }
  };

  const handleStart = async (id) => {
    try {
      await startScene(id);
      toast.success('Szene gestartet');
      fetchScenes();
    } catch (error) {
      console.error('Error starting scene:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Starten der Szene'));
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (savingNote) return;
    setSavingNote(true);
    setFormErrorNote(null);
    try {
      await addSceneNote(selectedScene.id, { note: noteText });
      setShowNoteModal(false);
      setNoteText('');
      setSelectedScene(null);
      toast.success('Notiz hinzugefügt');
      fetchScenes();
    } catch (error) {
      console.error('Error adding note:', error);
      setFormErrorNote(extractErrorMessage(error, 'Fehler beim Hinzufügen der Notiz'));
    } finally {
      setSavingNote(false);
    }
  };

  const handleEnd = async (e) => {
    e.preventDefault();
    if (savingEnd) return;
    setSavingEnd(true);
    setFormErrorEnd(null);
    try {
      await endScene(selectedScene.id, endData);
      setShowEndModal(false);
      setEndData({ summary: '', rating: 5 });
      setSelectedScene(null);
      toast.success('Szene beendet');
      fetchScenes();
    } catch (error) {
      console.error('Error ending scene:', error);
      setFormErrorEnd(extractErrorMessage(error, 'Fehler beim Beenden der Szene'));
    } finally {
      setSavingEnd(false);
    }
  };

  const handleArchive = async (id) => {
    try {
      await archiveScene(id);
      toast.success('Szene archiviert');
      fetchScenes();
    } catch (error) {
      console.error('Error archiving scene:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Archivieren der Szene'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Szene wirklich löschen?')) return;
    try {
      await deleteScene(id);
      toast.success('Szene gelöscht');
      fetchScenes();
    } catch (error) {
      console.error('Error deleting scene:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen der Szene'));
    }
  };

  const closeCreateModal = () => {
    if (savingCreate) return;
    setShowCreateModal(false);
    setFormErrorCreate(null);
  };

  const closeNoteModal = () => {
    if (savingNote) return;
    setShowNoteModal(false);
    setFormErrorNote(null);
  };

  const closeEndModal = () => {
    if (savingEnd) return;
    setShowEndModal(false);
    setFormErrorEnd(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      planned: { class: 'planned', label: 'Geplant' },
      active: { class: 'active', label: 'Aktiv' },
      completed: { class: 'completed', label: 'Abgeschlossen' },
      archived: { class: 'archived', label: 'Archiviert' }
    };
    return badges[status] || badges.planned;
  };

  const getTypeBadge = (type) => {
    const badges = {
      rp: { class: 'rp', label: 'RP' },
      erp: { class: 'erp', label: 'ERP' },
      combat: { class: 'combat', label: 'Kampf' },
      social: { class: 'social', label: 'Sozial' },
      event: { class: 'event', label: 'Event' }
    };
    return badges[type] || badges.rp;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Szenen</h1>
          <button className="add-btn" onClick={() => setShowCreateModal(true)}>
            <FaPlus /> Neue Szene
          </button>
        </div>

        <div className="filter-tabs">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Alle</button>
          <button className={filter === 'planned' ? 'active' : ''} onClick={() => setFilter('planned')}>Geplant</button>
          <button className={filter === 'active' ? 'active' : ''} onClick={() => setFilter('active')}>Aktiv</button>
          <button className={filter === 'completed' ? 'active' : ''} onClick={() => setFilter('completed')}>Abgeschlossen</button>
          <button className={filter === 'archived' ? 'active' : ''} onClick={() => setFilter('archived')}>Archiviert</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="scenes-grid">
            {scenes.length > 0 ? (
              scenes.map(scene => {
                const statusBadge = getStatusBadge(scene.status);
                const typeBadge = getTypeBadge(scene.type);
                return (
                  <div key={scene.id} className={`scene-card ${scene.status}`}>
                    <div className="scene-header">
                      <span className={`type-badge ${typeBadge.class}`}>{typeBadge.label}</span>
                      <span className={`status-badge ${statusBadge.class}`}>{statusBadge.label}</span>
                    </div>
                    <h3 className="scene-title">{scene.title}</h3>
                    {scene.description && <p className="scene-desc">{scene.description}</p>}
                    <div className="scene-meta">
                      {scene.setting && <span className="meta-item setting">{scene.setting}</span>}
                      {scene.characters && <span className="meta-item characters">{scene.characters}</span>}
                      {scene.planned_date && (
                        <span className="meta-item date">
                          {new Date(scene.planned_date).toLocaleDateString('de-DE')}
                        </span>
                      )}
                      {scene.rating != null && (
                        <span className="meta-item rating">{scene.rating}/10</span>
                      )}
                    </div>
                    {scene.summary && <p className="scene-summary">{scene.summary}</p>}
                    <div className="scene-actions">
                      {scene.status === 'planned' && (
                        <button className="action-btn start" onClick={() => handleStart(scene.id)} title="Starten">
                          <FaPlay /> Start
                        </button>
                      )}
                      {scene.status === 'active' && (
                        <>
                          <button className="action-btn note" onClick={() => { setSelectedScene(scene); setShowNoteModal(true); }} title="Notiz">
                            <FaStickyNote />
                          </button>
                          <button className="action-btn end" onClick={() => { setSelectedScene(scene); setShowEndModal(true); }} title="Beenden">
                            <FaStop /> Ende
                          </button>
                        </>
                      )}
                      {scene.status === 'completed' && (
                        <button className="action-btn archive" onClick={() => handleArchive(scene.id)} title="Archivieren">
                          <FaArchive />
                        </button>
                      )}
                      {user.isAdmin && (
                        <button className="action-btn delete" onClick={() => handleDelete(scene.id)} title="Löschen">
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-data">Keine Szenen gefunden</div>
            )}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={closeCreateModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Szene</h2>
              {formErrorCreate && <div className="shared-form-error-banner">{formErrorCreate}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <label>Titel *</label>
                  <input
                    type="text"
                    value={newScene.title}
                    onChange={e => setNewScene({...newScene, title: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={newScene.description}
                    onChange={e => setNewScene({...newScene, description: e.target.value})}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Typ</label>
                    <select
                      value={newScene.type}
                      onChange={e => setNewScene({...newScene, type: e.target.value})}
                    >
                      <option value="rp">RP</option>
                      <option value="erp">ERP</option>
                      <option value="combat">Kampf</option>
                      <option value="social">Sozial</option>
                      <option value="event">Event</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Geplantes Datum</label>
                    <input
                      type="datetime-local"
                      value={newScene.planned_date}
                      onChange={e => setNewScene({...newScene, planned_date: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Setting/Ort</label>
                  <input
                    type="text"
                    value={newScene.setting}
                    onChange={e => setNewScene({...newScene, setting: e.target.value})}
                    placeholder="z.B. Ul'dah, Quicksand..."
                  />
                </div>
                <div className="form-group">
                  <label>Charaktere</label>
                  <input
                    type="text"
                    value={newScene.characters}
                    onChange={e => setNewScene({...newScene, characters: e.target.value})}
                    placeholder="Beteiligte Charaktere..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeCreateModal} disabled={savingCreate}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingCreate}>
                    {savingCreate ? 'Speichert...' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Note Modal */}
        {showNoteModal && selectedScene && (
          <div className="modal-overlay" onClick={closeNoteModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Notiz hinzufügen</h2>
              {formErrorNote && <div className="shared-form-error-banner">{formErrorNote}</div>}
              <p className="modal-subtitle">Szene: {selectedScene.title}</p>
              <form onSubmit={handleAddNote}>
                <div className="form-group">
                  <label>Notiz *</label>
                  <textarea
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    required
                    placeholder="Was ist passiert..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeNoteModal} disabled={savingNote}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingNote}>
                    {savingNote ? 'Speichert...' : 'Speichern'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* End Scene Modal */}
        {showEndModal && selectedScene && (
          <div className="modal-overlay" onClick={closeEndModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Szene beenden</h2>
              {formErrorEnd && <div className="shared-form-error-banner">{formErrorEnd}</div>}
              <p className="modal-subtitle">Szene: {selectedScene.title}</p>
              <form onSubmit={handleEnd}>
                <div className="form-group">
                  <label>Zusammenfassung</label>
                  <textarea
                    value={endData.summary}
                    onChange={e => setEndData({...endData, summary: e.target.value})}
                    placeholder="Zusammenfassung der Szene..."
                  />
                </div>
                <div className="form-group">
                  <label>Bewertung: {endData.rating}/10</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={endData.rating}
                    onChange={e => setEndData({...endData, rating: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeEndModal} disabled={savingEnd}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingEnd}>
                    {savingEnd ? 'Speichert...' : 'Szene beenden'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Scenes;
